import { randomBytes } from './bytes';
import { checkMnemonic } from './mnemonic';

// Optional encrypted backup of the recovery phrase (format v1, see docs/BACKUP-FORMAT.md).
// PBKDF2-HMAC-SHA256 + AES-256-GCM with a password chosen by the user. The file is
// identical on every platform (web, iPhone, Android).

export const BACKUP_ITERATIONS = 600_000;
export const BACKUP_MIN_PASSWORD_LENGTH = 10;
const MIN_ITERATIONS = 100_000;
const MAX_ITERATIONS = 10_000_000;
const AAD = new TextEncoder().encode('coinwow-backup-v1');

export type BackupErrorKind =
  | 'notABackup'
  | 'unsupported'
  | 'wrongNetwork'
  | 'wrongPasswordOrAltered'
  | 'invalidPhrase';

export class BackupError extends Error {
  constructor(public readonly kind: BackupErrorKind) {
    super(kind);
  }
}

export function passwordLongEnough(password: string): boolean {
  return [...password].length >= BACKUP_MIN_PASSWORD_LENGTH;
}

const toB64 = (b: Uint8Array) => {
  let s = '';
  for (const x of b) s += String.fromCharCode(x);
  return btoa(s);
};

const fromB64 = (s: string): Uint8Array => {
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(s)) throw new Error('base64');
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
};

async function deriveKey(password: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const pw = new TextEncoder().encode(password.normalize('NFKC'));
  const base = await crypto.subtle.importKey('raw', pw as BufferSource, 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Builds the backup file content. `salt` and `nonce` are for tests only (fresh random by default). */
export async function createBackup(
  phrase: string,
  password: string,
  network = 'mainnet',
  opts: { iterations?: number; salt?: Uint8Array; nonce?: Uint8Array } = {},
): Promise<string> {
  const iterations = opts.iterations ?? BACKUP_ITERATIONS;
  const salt = opts.salt ?? randomBytes(16);
  const nonce = opts.nonce ?? randomBytes(12);
  const key = await deriveKey(password, salt, iterations);
  const plain = new TextEncoder().encode(checkMnemonic(phrase));
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv: nonce as BufferSource, additionalData: AAD as BufferSource, tagLength: 128 }, key, plain as BufferSource),
  );
  const file = {
    format: 'coinwow-backup',
    version: 1,
    network,
    kdf: 'pbkdf2-hmac-sha256',
    iterations,
    salt: toB64(salt),
    cipher: 'aes-256-gcm',
    nonce: toB64(nonce),
    ciphertext: toB64(ct),
  };
  return JSON.stringify(file, null, 2) + '\n';
}

/** Decrypts a backup file and returns the recovery phrase. Throws BackupError. */
export async function restoreBackup(content: string, password: string, network = 'mainnet'): Promise<string> {
  if (content.length > 4096) throw new BackupError('notABackup');
  let f: Record<string, unknown>;
  try {
    const parsed = JSON.parse(content);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) throw new Error();
    f = parsed as Record<string, unknown>;
  } catch {
    throw new BackupError('notABackup');
  }
  if (f.format !== 'coinwow-backup') throw new BackupError('notABackup');
  if (f.version !== 1 || f.kdf !== 'pbkdf2-hmac-sha256' || f.cipher !== 'aes-256-gcm') throw new BackupError('unsupported');
  if (f.network !== network) throw new BackupError('wrongNetwork');
  let salt: Uint8Array, nonce: Uint8Array, ct: Uint8Array;
  const iterations = f.iterations;
  try {
    if (typeof f.salt !== 'string' || typeof f.nonce !== 'string' || typeof f.ciphertext !== 'string') throw new Error();
    salt = fromB64(f.salt);
    nonce = fromB64(f.nonce);
    ct = fromB64(f.ciphertext);
  } catch {
    throw new BackupError('notABackup');
  }
  if (
    typeof iterations !== 'number' || !Number.isInteger(iterations) ||
    iterations < MIN_ITERATIONS || iterations > MAX_ITERATIONS ||
    salt.length !== 16 || nonce.length !== 12 || ct.length < 17 || ct.length > 1024
  ) {
    throw new BackupError('unsupported');
  }
  let plain: Uint8Array;
  try {
    const key = await deriveKey(password, salt, iterations);
    plain = new Uint8Array(
      await crypto.subtle.decrypt({ name: 'AES-GCM', iv: nonce as BufferSource, additionalData: AAD as BufferSource, tagLength: 128 }, key, ct as BufferSource),
    );
  } catch {
    throw new BackupError('wrongPasswordOrAltered');
  }
  try {
    return checkMnemonic(new TextDecoder().decode(plain));
  } catch {
    throw new BackupError('invalidPhrase');
  }
}
